import { useState, useEffect } from "react";


/**
 * Custom hook to validate and manage an image URL.
 * 
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.remoteUrl - The URL of the remote image to validate.
 * @param {string} [params.defaultUrl=""] - The fallback URL to use if the remote image is invalid or not provided.
 * 
 * @returns {Object} - An object containing:
 *   @property {string} imageUrl - The validated image URL (either the remote URL if valid, or the default URL).
 *   @property {boolean} isValid - A boolean indicating whether the remote image URL is valid.
 */
const useValidatedImage = ({ remoteUrl, defaultUrl = "" }) => {
  const [imageUrl, setImageUrl] = useState(remoteUrl || defaultUrl);
  const [isValid, setIsValid] = useState(!!remoteUrl);

  useEffect(() => {
    if (!remoteUrl) {
      setImageUrl(defaultUrl);
      setIsValid(false);
      return;
    }

    const img = new Image();
    img.src = remoteUrl;
    img.onload = () => {
      setImageUrl(remoteUrl);
      setIsValid(true);
    };
    img.onerror = () => {
      setImageUrl(defaultUrl);
      setIsValid(false);
    };
  }, [remoteUrl, defaultUrl]);

  return { imageUrl, isValid };
};

export default useValidatedImage;
